import { request, requestBlob, requestForm, requestNoContent } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/**
 * Trạng thái của một dự án.
 *
 * Giá trị số phải khớp với phía máy chủ. Không dùng chuỗi tên trạng thái: đổi câu chữ tiếng Việt về
 * sau sẽ làm dữ liệu cũ thành vô nghĩa.
 */
export const ProjectStatus = {
  /** Nháp hoặc đang chuẩn bị — chưa họp khởi động. */
  Draft: 1,

  /** Đang thực hiện. */
  InProgress: 2,

  /** Tạm dừng. Bắt buộc kèm lý do. */
  OnHold: 3,

  /** Đang lắp đặt tại nhà máy khách. */
  Installing: 4,

  /** Đã xong, chờ khách nghiệm thu. */
  PendingAcceptance: 5,

  /** Đã nghiệm thu, đang trong thời gian bảo hành. */
  UnderWarranty: 6,

  /** Đã đóng — hết bảo hành, đã thanh lý, đã tổng kết. */
  Closed: 7,

  /** Đã hủy. */
  Cancelled: 8,
} as const;

/** Giá trị của trạng thái dự án. */
export type ProjectStatusValue = (typeof ProjectStatus)[keyof typeof ProjectStatus];

/** Mức độ ưu tiên của dự án. */
export const ProjectPriority = {
  /** Rất cao. */
  Critical: 1,

  /** Cao. */
  High: 2,

  /** Bình thường. */
  Normal: 3,

  /** Thấp. */
  Low: 4,
} as const;

/** Giá trị của mức độ ưu tiên. */
export type ProjectPriorityValue = (typeof ProjectPriority)[keyof typeof ProjectPriority];

/**
 * Tình trạng sức khỏe của dự án.
 *
 * Khác trạng thái: trạng thái nói dự án đang ở giai đoạn nào, còn sức khỏe nói nó có ổn không. Một
 * dự án đang thực hiện đúng giai đoạn vẫn có thể đang ở mức nghiêm trọng.
 */
export const ProjectHealth = {
  /** Tốt — đúng kế hoạch. */
  Good: 1,

  /** Cần chú ý — có dấu hiệu chậm nhưng còn xử lý được. */
  NeedsAttention: 2,

  /** Có vấn đề — đã chậm hoặc vượt ngân sách, cần can thiệp. */
  AtRisk: 3,

  /** Nghiêm trọng — cần quyết định của cấp trên. */
  Critical: 4,
} as const;

/** Giá trị của tình trạng sức khỏe. */
export type ProjectHealthValue = (typeof ProjectHealth)[keyof typeof ProjectHealth];

/** Vai trò của một người trong dự án. */
export const ProjectMemberRole = {
  /** Người quản lý dự án. */
  ProjectManager: 1,

  /** Trưởng nhóm kỹ thuật. */
  TechnicalLead: 2,

  /** Kỹ sư thị giác máy. */
  VisionEngineer: 3,

  /** Kỹ sư điều khiển khả trình và robot. */
  ControlEngineer: 4,

  /** Kỹ sư thiết kế cơ khí. */
  MechanicalDesigner: 5,

  /** Kỹ sư thiết kế điện. */
  ElectricalDesigner: 6,

  /** Kỹ thuật viên lắp ráp. */
  AssemblyTechnician: 7,

  /** Kỹ sư dịch vụ — bảo hành và hỗ trợ sau bàn giao. */
  ServiceEngineer: 8,

  /** Người phụ trách mua hàng cho dự án. */
  PurchasingOfficer: 9,

  /** Kế toán theo dõi dự án. */
  ProjectAccountant: 10,

  /** Người theo dõi — chỉ đọc, không được phân việc. */
  Observer: 11,
} as const;

/** Giá trị của vai trò trong dự án. */
export type ProjectMemberRoleValue =
  (typeof ProjectMemberRole)[keyof typeof ProjectMemberRole];

/** Tên tiếng Việt của từng trạng thái dự án. */
export const projectStatusNames: Readonly<Record<ProjectStatusValue, string>> = {
  [ProjectStatus.Draft]: 'Nháp',
  [ProjectStatus.InProgress]: 'Đang thực hiện',
  [ProjectStatus.OnHold]: 'Tạm dừng',
  [ProjectStatus.Installing]: 'Đang lắp đặt',
  [ProjectStatus.PendingAcceptance]: 'Chờ nghiệm thu',
  [ProjectStatus.UnderWarranty]: 'Đang bảo hành',
  [ProjectStatus.Closed]: 'Đã đóng',
  [ProjectStatus.Cancelled]: 'Đã hủy',
};

/** Tên tiếng Việt của từng mức độ ưu tiên. */
export const projectPriorityNames: Readonly<Record<ProjectPriorityValue, string>> = {
  [ProjectPriority.Critical]: 'Rất cao',
  [ProjectPriority.High]: 'Cao',
  [ProjectPriority.Normal]: 'Bình thường',
  [ProjectPriority.Low]: 'Thấp',
};

/** Tên tiếng Việt của từng mức sức khỏe. */
export const projectHealthNames: Readonly<Record<ProjectHealthValue, string>> = {
  [ProjectHealth.Good]: 'Tốt',
  [ProjectHealth.NeedsAttention]: 'Cần chú ý',
  [ProjectHealth.AtRisk]: 'Có vấn đề',
  [ProjectHealth.Critical]: 'Nghiêm trọng',
};

/** Tên tiếng Việt của từng vai trò trong dự án. */
export const projectMemberRoleNames: Readonly<Record<ProjectMemberRoleValue, string>> = {
  [ProjectMemberRole.ProjectManager]: 'Quản lý dự án',
  [ProjectMemberRole.TechnicalLead]: 'Trưởng nhóm kỹ thuật',
  [ProjectMemberRole.VisionEngineer]: 'Kỹ sư thị giác',
  [ProjectMemberRole.ControlEngineer]: 'Kỹ sư PLC / Robot',
  [ProjectMemberRole.MechanicalDesigner]: 'Thiết kế cơ khí',
  [ProjectMemberRole.ElectricalDesigner]: 'Thiết kế điện',
  [ProjectMemberRole.AssemblyTechnician]: 'Kỹ thuật viên lắp ráp',
  [ProjectMemberRole.ServiceEngineer]: 'Kỹ sư dịch vụ',
  [ProjectMemberRole.PurchasingOfficer]: 'Mua hàng',
  [ProjectMemberRole.ProjectAccountant]: 'Kế toán dự án',
  [ProjectMemberRole.Observer]: 'Người theo dõi',
};

/** Thông tin rút gọn của dự án, dùng cho danh sách và bảng Kanban. */
export interface ProjectListItem {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly projectTypeId: string;
  readonly projectTypeName: string;
  readonly customerId: string;
  readonly projectManagerUserId: string;
  readonly status: ProjectStatusValue;
  readonly priority: ProjectPriorityValue;
  readonly health: ProjectHealthValue;
  readonly plannedProgressPercent: number;
  readonly actualProgressPercent: number;
  readonly plannedEndDate: string | null;

  /**
   * Mốc cam kết đang có hiệu lực: ngày gia hạn nếu có, không thì ngày cam kết trong hợp đồng.
   *
   * Máy chủ tính sẵn. Không bao giờ là ngày kế hoạch nội bộ — kế hoạch nội bộ thường đặt sớm hơn để
   * có đệm, nên lấy nhầm sẽ báo quá hạn mỗi khi đội kỹ thuật dùng hết phần đệm.
   */
  readonly committedCompletionDate: string | null;

  /** Đã quá mốc cam kết mà chưa kết thúc thực tế. Máy chủ tính sẵn. */
  readonly isOverdue: boolean;

  /** Chưa đóng và chưa hủy. */
  readonly isOpen: boolean;

  /** Dự án nội bộ hoặc nghiên cứu. */
  readonly isInternal: boolean;
}

/** Thông tin đầy đủ của một dự án. */
export interface ProjectDetail extends ProjectListItem {
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly scopeSummary: string | null;
  readonly installationCustomerId: string | null;
  readonly technicalLeadUserId: string | null;
  readonly sponsorUserId: string | null;
  readonly plannedStartDate: string | null;
  readonly contractCompletionDate: string | null;
  readonly extendedCompletionDate: string | null;
  readonly actualStartDate: string | null;
  readonly actualEndDate: string | null;
  readonly acceptanceDate: string | null;
  readonly warrantyMonths: number | null;
  readonly warrantyEndDate: string | null;
  readonly statusChangedAt: string | null;
  readonly holdReason: string | null;
  readonly healthReason: string | null;
  readonly isWarrantyExpiringSoon: boolean;
}

/** Điều kiện lọc danh sách dự án. */
export interface ProjectFilter {
  readonly searchTerm?: string;
  readonly status?: ProjectStatusValue;
  readonly onlyOpen?: boolean;
  readonly health?: ProjectHealthValue;
  readonly priority?: ProjectPriorityValue;
  readonly projectTypeId?: string;
  readonly customerId?: string;
  readonly projectManagerUserId?: string;
  readonly memberUserId?: string;
  readonly isInternal?: boolean;
  readonly overdueOnly?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi mở dự án mới. */
export interface CreateProjectPayload {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly projectTypeId: string;
  readonly customerId: string;

  /** Rỗng nghĩa là chính người đang mở dự án. */
  readonly projectManagerUserId: string | null;
  readonly priority: ProjectPriorityValue;
  readonly isInternal: boolean;
  readonly description: string | null;
}

/** Nội dung gửi lên khi sửa tên và mô tả dự án. */
export interface UpdateProjectIdentityPayload {
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly scopeSummary: string | null;
}

/** Nội dung gửi lên khi sửa phân công dự án. */
export interface UpdateProjectAssignmentPayload {
  readonly projectManagerUserId: string;
  readonly technicalLeadUserId: string | null;
  readonly sponsorUserId: string | null;
  readonly installationCustomerId: string | null;
}

/** Nội dung gửi lên khi sửa lịch trình dự án. */
export interface UpdateProjectSchedulePayload {
  readonly plannedStartDate: string | null;
  readonly plannedEndDate: string | null;
  readonly contractCompletionDate: string | null;
  readonly extendedCompletionDate: string | null;
  readonly actualStartDate: string | null;
  readonly actualEndDate: string | null;
}

/** Nội dung gửi lên khi ghi nhận nghiệm thu. */
export interface RecordProjectAcceptancePayload {
  readonly acceptanceDate: string;
  readonly warrantyMonths: number;
}

/** Nội dung gửi lên khi đổi trạng thái dự án. */
export interface ChangeProjectStatusPayload {
  readonly status: ProjectStatusValue;
  readonly holdReason: string | null;
}

/** Nội dung gửi lên khi cập nhật tiến độ và tình trạng dự án. */
export interface UpdateProjectProgressPayload {
  readonly plannedProgressPercent: number;
  readonly actualProgressPercent: number;
  readonly health: ProjectHealthValue;
  readonly healthReason: string | null;
}

/** Một loại dự án trong danh mục. */
export interface ProjectType {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly codePrefix: string | null;
  readonly defaultDurationMonths: number | null;
  readonly defaultWarrantyMonths: number | null;
  readonly requiresVisionConfiguration: boolean;
  readonly requiresInstallationOnsite: boolean;
  readonly sortOrder: number;
  readonly isActive: boolean;

  /**
   * Số dự án chưa đóng thuộc loại này.
   *
   * Máy chủ đếm sẵn để giao diện vô hiệu hóa nút ngừng dùng thay vì để người dùng bấm rồi nhận lỗi.
   */
  readonly openProjectCount: number;
}

/** Điều kiện lọc danh mục loại dự án. */
export interface ProjectTypeFilter {
  readonly searchTerm?: string;
  readonly onlyActive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi khai loại dự án mới. */
export interface CreateProjectTypePayload {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa tên và mô tả loại dự án. */
export interface UpdateProjectTypeIdentityPayload {
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa giá trị mặc định của loại dự án. */
export interface UpdateProjectTypeDefaultsPayload {
  readonly codePrefix: string | null;
  readonly defaultDurationMonths: number | null;
  readonly defaultWarrantyMonths: number | null;
  readonly requiresVisionConfiguration: boolean;
  readonly requiresInstallationOnsite: boolean;
}

/** Một thành viên dự án. */
export interface ProjectMember {
  readonly id: string;
  readonly projectId: string;
  readonly userAccountId: string;
  readonly role: ProjectMemberRoleValue;
  readonly allocationPercent: number | null;
  readonly joinedAt: string;
  readonly leftAt: string | null;
  readonly isActive: boolean;

  /** Chính là người quản lý dự án hiện tại. Máy chủ đối chiếu, giao diện không tự suy. */
  readonly isProjectManager: boolean;
}

/** Nội dung gửi lên khi thêm một người vào dự án. */
export interface AddProjectMemberPayload {
  readonly userAccountId: string;
  readonly role: ProjectMemberRoleValue;
  readonly joinedAt: string | null;
  readonly allocationPercent: number | null;
}

/** Nội dung gửi lên khi sửa vai trò và phần trăm thời gian của một thành viên. */
export interface UpdateProjectMemberPayload {
  readonly role: ProjectMemberRoleValue;
  readonly allocationPercent: number | null;
}

/** Tổng phần trăm thời gian một người đang được phân bổ. */
export interface ProjectWorkloadItem {
  readonly userAccountId: string;
  readonly activeProjectCount: number;
  readonly totalAllocationPercent: number;
  readonly isOverloaded: boolean;
}

const projectRoute = '/api/v1/projects';
const projectTypeRoute = '/api/v1/projects/project-types';
const workloadRoute = '/api/v1/projects/workload';

/**
 * Dựng phần truy vấn của địa chỉ từ một tập tham số.
 *
 * Bỏ hẳn tham số rỗng thay vì gửi chuỗi trống: máy chủ phân biệt "không lọc" với "lọc theo chuỗi
 * trống", và gửi chuỗi trống sẽ thành điều kiện lọc thật.
 */
function buildQueryString(values: Readonly<Record<string, unknown>>): string {
  const parameters = new URLSearchParams();

  for (const [key, value] of Object.entries(values)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (trimmed !== '') {
        parameters.set(key, trimmed);
      }

      continue;
    }

    if (typeof value === 'boolean') {
      // Chỉ gửi khi bật. Gửi false cũng là một điều kiện lọc thật với những tham số ba trạng thái
      // như isInternal, nên nơi gọi phải truyền undefined khi muốn "không lọc".
      if (value) {
        parameters.set(key, 'true');
      }

      continue;
    }

    // Chỉ còn số ở nhánh này: chuỗi, luận lý, rỗng và không xác định đã được xử lý ở trên.
    if (typeof value === 'number') {
      parameters.set(key, value.toString());
    }
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách dự án. */
export async function listProjects(
  filter: ProjectFilter,
): Promise<PagedResponse<ProjectListItem>> {
  return await request<PagedResponse<ProjectListItem>>(
    `${projectRoute}${buildQueryString({ ...filter })}`,
  );
}

/** Lấy chi tiết một dự án. */
export async function getProject(id: string): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}`);
}

/** Mở một dự án mới. */
export async function createProject(payload: CreateProjectPayload): Promise<ProjectDetail> {
  return await request<ProjectDetail>(projectRoute, { method: 'POST', body: payload });
}

/** Xóa mềm một dự án. Chỉ dự án còn ở trạng thái nháp mới xóa được. */
export async function deleteProject(id: string): Promise<void> {
  await requestNoContent(`${projectRoute}/${id}`, { method: 'DELETE' });
}

/** Sửa tên và mô tả dự án. */
export async function updateProjectIdentity(
  id: string,
  payload: UpdateProjectIdentityPayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/identity`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa phân công dự án. */
export async function updateProjectAssignment(
  id: string,
  payload: UpdateProjectAssignmentPayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/assignment`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa lịch trình dự án. */
export async function updateProjectSchedule(
  id: string,
  payload: UpdateProjectSchedulePayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/schedule`, {
    method: 'PUT',
    body: payload,
  });
}

/** Ghi nhận nghiệm thu và chốt hạn bảo hành. */
export async function recordProjectAcceptance(
  id: string,
  payload: RecordProjectAcceptancePayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/acceptance`, {
    method: 'POST',
    body: payload,
  });
}

/** Đổi trạng thái dự án. */
export async function changeProjectStatus(
  id: string,
  payload: ChangeProjectStatusPayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/status`, {
    method: 'PUT',
    body: payload,
  });
}

/** Đổi mức độ ưu tiên. */
export async function changeProjectPriority(
  id: string,
  priority: ProjectPriorityValue,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/priority`, {
    method: 'PUT',
    body: { priority },
  });
}

/** Cập nhật tiến độ và tình trạng dự án. */
export async function updateProjectProgress(
  id: string,
  payload: UpdateProjectProgressPayload,
): Promise<ProjectDetail> {
  return await request<ProjectDetail>(`${projectRoute}/${id}/progress`, {
    method: 'PUT',
    body: payload,
  });
}

/** Lấy một trang danh mục loại dự án. */
export async function listProjectTypes(
  filter: ProjectTypeFilter,
): Promise<PagedResponse<ProjectType>> {
  return await request<PagedResponse<ProjectType>>(
    `${projectTypeRoute}${buildQueryString({ ...filter })}`,
  );
}

/** Khai một loại dự án mới. */
export async function createProjectType(
  payload: CreateProjectTypePayload,
): Promise<ProjectType> {
  return await request<ProjectType>(projectTypeRoute, { method: 'POST', body: payload });
}

/** Sửa tên và mô tả loại dự án. */
export async function updateProjectTypeIdentity(
  id: string,
  payload: UpdateProjectTypeIdentityPayload,
): Promise<ProjectType> {
  return await request<ProjectType>(`${projectTypeRoute}/${id}/identity`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa giá trị mặc định của loại dự án. */
export async function updateProjectTypeDefaults(
  id: string,
  payload: UpdateProjectTypeDefaultsPayload,
): Promise<ProjectType> {
  return await request<ProjectType>(`${projectTypeRoute}/${id}/defaults`, {
    method: 'PUT',
    body: payload,
  });
}

/** Ngừng dùng hoặc cho dùng lại một loại dự án. */
export async function setProjectTypeActive(
  id: string,
  isActive: boolean,
): Promise<ProjectType> {
  const action = isActive ? 'activate' : 'deactivate';

  return await request<ProjectType>(`${projectTypeRoute}/${id}/${action}`, { method: 'POST' });
}

/** Lấy danh sách thành viên của một dự án, gồm cả người đã rời. */
export async function listProjectMembers(projectId: string): Promise<readonly ProjectMember[]> {
  return await request<readonly ProjectMember[]>(`${projectRoute}/${projectId}/members`);
}

/** Thêm một người vào dự án. */
export async function addProjectMember(
  projectId: string,
  payload: AddProjectMemberPayload,
): Promise<ProjectMember> {
  return await request<ProjectMember>(`${projectRoute}/${projectId}/members`, {
    method: 'POST',
    body: payload,
  });
}

/** Sửa vai trò và phần trăm thời gian của một thành viên. */
export async function updateProjectMember(
  projectId: string,
  memberId: string,
  payload: UpdateProjectMemberPayload,
): Promise<ProjectMember> {
  return await request<ProjectMember>(`${projectRoute}/${projectId}/members/${memberId}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Ghi nhận một thành viên đã rời dự án. */
export async function leaveProject(
  projectId: string,
  memberId: string,
  leftAt: string | null,
): Promise<ProjectMember> {
  return await request<ProjectMember>(
    `${projectRoute}/${projectId}/members/${memberId}/leave`,
    { method: 'POST', body: { leftAt } },
  );
}

/** Cho một thành viên đã rời tham gia lại dự án. */
export async function rejoinProject(
  projectId: string,
  memberId: string,
  allocationPercent: number | null,
): Promise<ProjectMember> {
  return await request<ProjectMember>(
    `${projectRoute}/${projectId}/members/${memberId}/rejoin`,
    { method: 'POST', body: { allocationPercent } },
  );
}

/** Lấy tổng phần trăm thời gian đang phân bổ của từng người. */
export async function getProjectWorkload(): Promise<readonly ProjectWorkloadItem[]> {
  return await request<readonly ProjectWorkloadItem[]>(workloadRoute);
}

/**
 * Trạng thái của một công việc trong dự án.
 *
 * Giá trị 3 — bị chặn — là giá trị đáng giá nhất: phần lớn công cụ chỉ có chưa làm, đang làm và xong,
 * nên một việc đang chờ khách gửi mẫu vẫn hiện là đang làm, và người quản lý không phân biệt được đội
 * đang bận với đội đang chờ.
 */
export const ProjectTaskStatus = {
  /** Chưa bắt đầu. */
  NotStarted: 1,

  /** Đang làm. */
  InProgress: 2,

  /** Bị chặn — bắt buộc kèm lý do. */
  Blocked: 3,

  /** Đã làm xong phần việc, đang chờ duyệt hoặc chờ khách phản hồi. */
  PendingReview: 4,

  /** Hoàn thành. */
  Completed: 5,

  /** Đã hủy. */
  Cancelled: 6,
} as const;

/** Giá trị của trạng thái công việc. */
export type ProjectTaskStatusValue =
  (typeof ProjectTaskStatus)[keyof typeof ProjectTaskStatus];

/** Cách đo tiến độ của một công việc. */
export const ProjectTaskProgressMethod = {
  /** Không phần trăm: chưa xong là 0, xong là 100. Mặc định của mọi việc mới. */
  ZeroOrHundred: 1,

  /** Theo danh sách kiểm. Chưa dùng được ở lát cắt này. */
  Checklist: 2,

  /** Theo mốc con. Chưa dùng được ở lát cắt này. */
  Milestones: 3,

  /** Người làm tự nhập phần trăm. Cách đo kém tin cậy nhất. */
  SelfReported: 4,
} as const;

/** Giá trị của cách đo tiến độ. */
export type ProjectTaskProgressMethodValue =
  (typeof ProjectTaskProgressMethod)[keyof typeof ProjectTaskProgressMethod];

/** Kiểu phụ thuộc giữa hai công việc. */
export const ProjectTaskDependencyType = {
  /** Xong việc trước mới bắt đầu việc sau. Phổ biến nhất. */
  FinishToStart: 1,

  /** Bắt đầu việc trước mới bắt đầu việc sau. */
  StartToStart: 2,

  /** Xong việc trước mới xong được việc sau. */
  FinishToFinish: 3,

  /** Bắt đầu việc trước mới xong được việc sau. */
  StartToFinish: 4,
} as const;

/** Giá trị của kiểu phụ thuộc. */
export type ProjectTaskDependencyTypeValue =
  (typeof ProjectTaskDependencyType)[keyof typeof ProjectTaskDependencyType];

/** Tên tiếng Việt của từng trạng thái công việc. */
export const projectTaskStatusNames: Readonly<Record<ProjectTaskStatusValue, string>> = {
  [ProjectTaskStatus.NotStarted]: 'Chưa bắt đầu',
  [ProjectTaskStatus.InProgress]: 'Đang làm',
  [ProjectTaskStatus.Blocked]: 'Bị chặn',
  [ProjectTaskStatus.PendingReview]: 'Chờ duyệt',
  [ProjectTaskStatus.Completed]: 'Hoàn thành',
  [ProjectTaskStatus.Cancelled]: 'Đã hủy',
};

/** Tên tiếng Việt của từng cách đo tiến độ. */
export const projectTaskProgressMethodNames: Readonly<
  Record<ProjectTaskProgressMethodValue, string>
> = {
  [ProjectTaskProgressMethod.ZeroOrHundred]: 'Xong hoặc chưa xong',
  [ProjectTaskProgressMethod.Checklist]: 'Theo danh sách kiểm',
  [ProjectTaskProgressMethod.Milestones]: 'Theo mốc con',
  [ProjectTaskProgressMethod.SelfReported]: 'Tự nhập phần trăm',
};

/** Tên tiếng Việt của từng kiểu phụ thuộc. */
export const projectTaskDependencyTypeNames: Readonly<
  Record<ProjectTaskDependencyTypeValue, string>
> = {
  [ProjectTaskDependencyType.FinishToStart]: 'Xong mới bắt đầu',
  [ProjectTaskDependencyType.StartToStart]: 'Bắt đầu cùng',
  [ProjectTaskDependencyType.FinishToFinish]: 'Xong cùng',
  [ProjectTaskDependencyType.StartToFinish]: 'Bắt đầu mới xong',
};

/** Một công việc trong cây, dạng phẳng kèm thông tin vị trí. */
export interface ProjectTaskItem {
  readonly id: string;
  readonly projectId: string;
  readonly parentId: string | null;

  /** Mã phân cấp sinh tự động từ vị trí trong cây, ví dụ 1.2.3. */
  readonly wbsCode: string;
  readonly code: string | null;
  readonly name: string;
  readonly description: string | null;
  readonly depth: number;
  readonly sortOrder: number;

  /** Có việc con. Thời lượng, giờ công và tiến độ của nó tính từ con lên. */
  readonly isSummaryTask: boolean;
  readonly isMilestone: boolean;
  readonly status: ProjectTaskStatusValue;
  readonly blockedReason: string | null;
  readonly priority: ProjectPriorityValue;
  readonly progressMethod: ProjectTaskProgressMethodValue;
  readonly progressPercent: number;

  /** Tiến độ là con số người làm tự báo, không phải thứ đếm được. */
  readonly isProgressSelfReported: boolean;
  readonly plannedStartDate: string | null;
  readonly plannedEndDate: string | null;
  readonly plannedDurationDays: number | null;
  readonly plannedEffortHours: number | null;
  readonly actualStartDate: string | null;
  readonly actualEndDate: string | null;
  readonly remainingEffortHours: number | null;
  readonly isOverdue: boolean;
}

/** Một quan hệ phụ thuộc giữa hai công việc. */
export interface ProjectTaskDependencyItem {
  readonly id: string;
  readonly predecessorTaskId: string;
  readonly successorTaskId: string;
  readonly dependencyType: ProjectTaskDependencyTypeValue;
  readonly lagDays: number;
  readonly isHardConstraint: boolean;
  readonly note: string | null;
}

/** Nội dung gửi lên khi tạo công việc mới. */
export interface CreateProjectTaskPayload {
  readonly name: string;
  readonly parentId: string | null;
  readonly code: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
  readonly isMilestone: boolean;
}

/** Nội dung gửi lên khi sửa công việc. */
export interface UpdateProjectTaskPayload {
  readonly code: string | null;
  readonly name: string;
  readonly description: string | null;
  readonly priority: ProjectPriorityValue;
  readonly isMilestone: boolean;
}

/** Nội dung gửi lên khi sửa lịch trình công việc. */
export interface UpdateProjectTaskSchedulePayload {
  readonly plannedStartDate: string | null;
  readonly plannedEndDate: string | null;
  readonly plannedDurationDays: number | null;
  readonly plannedEffortHours: number | null;
  readonly actualStartDate: string | null;
  readonly actualEndDate: string | null;
}

/** Nội dung gửi lên khi báo tiến độ công việc. */
export interface ReportProjectTaskProgressPayload {
  readonly progressPercent: number;
  readonly remainingEffortHours: number | null;

  /** Rỗng nghĩa là giữ nguyên cách đo hiện tại. */
  readonly progressMethod: ProjectTaskProgressMethodValue | null;
}

function taskRoute(projectId: string): string {
  return `${projectRoute}/${projectId}/tasks`;
}

function dependencyRoute(projectId: string): string {
  return `${projectRoute}/${projectId}/task-dependencies`;
}

/** Lấy toàn bộ cây công việc của một dự án, sắp theo thứ tự hiển thị. */
export async function listProjectTasks(
  projectId: string,
): Promise<readonly ProjectTaskItem[]> {
  return await request<readonly ProjectTaskItem[]>(taskRoute(projectId));
}

/** Tạo một công việc mới. */
export async function createProjectTask(
  projectId: string,
  payload: CreateProjectTaskPayload,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(taskRoute(projectId), {
    method: 'POST',
    body: payload,
  });
}

/** Sửa mã, tên, mô tả và mức ưu tiên của công việc. */
export async function updateProjectTask(
  projectId: string,
  taskId: string,
  payload: UpdateProjectTaskPayload,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}`, {
    method: 'PUT',
    body: payload,
  });
}

/** Sửa lịch trình công việc. */
export async function updateProjectTaskSchedule(
  projectId: string,
  taskId: string,
  payload: UpdateProjectTaskSchedulePayload,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/schedule`, {
    method: 'PUT',
    body: payload,
  });
}

/** Di chuyển công việc trong cây. */
export async function moveProjectTask(
  projectId: string,
  taskId: string,
  newParentId: string | null,
  sortOrder: number,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/position`, {
    method: 'PUT',
    body: { newParentId, sortOrder },
  });
}

/** Xóa mềm một công việc. Chỉ xóa được việc không còn việc con. */
export async function deleteProjectTask(projectId: string, taskId: string): Promise<void> {
  await requestNoContent(`${taskRoute(projectId)}/${taskId}`, { method: 'DELETE' });
}

/** Đổi trạng thái công việc. */
export async function changeProjectTaskStatus(
  projectId: string,
  taskId: string,
  status: ProjectTaskStatusValue,
  blockedReason: string | null,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/status`, {
    method: 'PUT',
    body: { status, blockedReason },
  });
}

/** Báo tiến độ công việc. */
export async function reportProjectTaskProgress(
  projectId: string,
  taskId: string,
  payload: ReportProjectTaskProgressPayload,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/progress`, {
    method: 'PUT',
    body: payload,
  });
}

/**
 * Đổi cách đo tiến độ của một công việc.
 *
 * Đường riêng, không gộp vào lời gọi báo tiến độ: chuyển sang đo bằng danh sách kiểm là quyết định
 * có ý thức và <b>không đi kèm một con số</b> — từ lúc đó phần trăm do máy chủ tính từ các mục đã
 * tick.
 */
export async function changeProjectTaskProgressMethod(
  projectId: string,
  taskId: string,
  progressMethod: ProjectTaskProgressMethodValue,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/progress-method`, {
    method: 'PUT',
    body: { progressMethod },
  });
}

/**
 * Khai lại giờ công còn lại mà không đụng tới phần trăm.
 *
 * Dùng được với mọi cách đo. Giờ công còn lại không suy ra được từ danh sách kiểm: tick xong tám
 * trên mười mục không có nghĩa là còn hai phần mười giờ công, vì hai mục cuối có thể là hai mục
 * nặng nhất.
 */
export async function reportProjectTaskRemainingEffort(
  projectId: string,
  taskId: string,
  remainingEffortHours: number | null,
): Promise<ProjectTaskItem> {
  return await request<ProjectTaskItem>(`${taskRoute(projectId)}/${taskId}/remaining-effort`, {
    method: 'PUT',
    body: { remainingEffortHours },
  });
}

/** Một mục trong danh sách kiểm của công việc. */
export interface ProjectTaskChecklistItem {
  readonly id: string;
  readonly projectTaskId: string;
  readonly sequenceNumber: number;
  readonly description: string;
  readonly isRequired: boolean;
  readonly weight: number;
  readonly isCompleted: boolean;
  readonly completedAt: string | null;
  readonly completedByUserId: string | null;
  readonly note: string | null;
}

/** Danh sách kiểm của một công việc kèm phần trăm đã tính. */
export interface ProjectTaskChecklist {
  readonly items: readonly ProjectTaskChecklistItem[];
  readonly progressPercent: number;
  readonly unfinishedRequiredCount: number;

  /**
   * Công việc có đang thật sự đo tiến độ bằng danh sách kiểm không.
   *
   * Một công việc được phép có danh sách kiểm mà vẫn đo tiến độ theo cách khác — khi đó danh sách
   * kiểm là ghi chú việc cần làm, không phải nguồn của con số.
   */
  readonly isProgressDrivenByChecklist: boolean;
}

/** Thông tin thêm hoặc sửa một mục kiểm. */
export interface SaveProjectTaskChecklistItemPayload {
  readonly description: string;
  readonly isRequired: boolean;
  readonly weight: number | null;
  readonly sequenceNumber: number;
  readonly note: string | null;
}

/** Lấy danh sách kiểm của một công việc. */
export async function getProjectTaskChecklist(
  projectId: string,
  taskId: string,
): Promise<ProjectTaskChecklist> {
  return await request<ProjectTaskChecklist>(`${taskRoute(projectId)}/${taskId}/checklist`);
}

/** Thêm một mục vào danh sách kiểm. */
export async function addProjectTaskChecklistItem(
  projectId: string,
  taskId: string,
  payload: SaveProjectTaskChecklistItemPayload,
): Promise<ProjectTaskChecklist> {
  return await request<ProjectTaskChecklist>(`${taskRoute(projectId)}/${taskId}/checklist`, {
    method: 'POST',
    body: payload,
  });
}

/** Sửa một mục kiểm. */
export async function updateProjectTaskChecklistItem(
  projectId: string,
  taskId: string,
  itemId: string,
  payload: SaveProjectTaskChecklistItemPayload,
): Promise<ProjectTaskChecklist> {
  return await request<ProjectTaskChecklist>(
    `${taskRoute(projectId)}/${taskId}/checklist/${itemId}`,
    { method: 'PUT', body: payload },
  );
}

/** Tick hoặc bỏ tick một mục kiểm. */
export async function setProjectTaskChecklistItemState(
  projectId: string,
  taskId: string,
  itemId: string,
  isCompleted: boolean,
): Promise<ProjectTaskChecklist> {
  return await request<ProjectTaskChecklist>(
    `${taskRoute(projectId)}/${taskId}/checklist/${itemId}/state`,
    { method: 'PUT', body: { isCompleted } },
  );
}

/** Xóa một mục kiểm. */
export async function removeProjectTaskChecklistItem(
  projectId: string,
  taskId: string,
  itemId: string,
): Promise<ProjectTaskChecklist> {
  return await request<ProjectTaskChecklist>(
    `${taskRoute(projectId)}/${taskId}/checklist/${itemId}`,
    { method: 'DELETE' },
  );
}

/** Một mốc con trong công việc. */
export interface ProjectTaskMilestone {
  readonly id: string;
  readonly projectTaskId: string;
  readonly sequenceNumber: number;
  readonly name: string;
  readonly progressPercent: number;
  readonly isReached: boolean;
  readonly reachedAt: string | null;
  readonly reachedByUserId: string | null;
}

/** Chuỗi mốc con của một công việc kèm phần trăm đã tính. */
export interface ProjectTaskMilestoneSet {
  readonly milestones: readonly ProjectTaskMilestone[];
  readonly progressPercent: number;

  /** Mốc đang đứng, rỗng khi chưa đạt mốc nào. */
  readonly reachedMilestoneId: string | null;

  /** Công việc có đang thật sự đo tiến độ bằng mốc con không. */
  readonly isProgressDrivenByMilestones: boolean;
}

/** Thông tin thêm hoặc sửa một mốc con. */
export interface SaveProjectTaskMilestonePayload {
  readonly name: string;
  readonly progressPercent: number;
  readonly sequenceNumber: number;
}

/** Lấy chuỗi mốc con của một công việc. */
export async function getProjectTaskMilestones(
  projectId: string,
  taskId: string,
): Promise<ProjectTaskMilestoneSet> {
  return await request<ProjectTaskMilestoneSet>(`${taskRoute(projectId)}/${taskId}/milestones`);
}

/** Thêm một mốc con. */
export async function addProjectTaskMilestone(
  projectId: string,
  taskId: string,
  payload: SaveProjectTaskMilestonePayload,
): Promise<ProjectTaskMilestoneSet> {
  return await request<ProjectTaskMilestoneSet>(`${taskRoute(projectId)}/${taskId}/milestones`, {
    method: 'POST',
    body: payload,
  });
}

/** Xóa một mốc con. */
export async function removeProjectTaskMilestone(
  projectId: string,
  taskId: string,
  milestoneId: string,
): Promise<ProjectTaskMilestoneSet> {
  return await request<ProjectTaskMilestoneSet>(
    `${taskRoute(projectId)}/${taskId}/milestones/${milestoneId}`,
    { method: 'DELETE' },
  );
}

/**
 * Chọn mốc đang đứng.
 *
 * <b>Chọn một mốc, không phải tick từng mốc.</b> Mốc con có thứ tự và cộng dồn, nên đạt mốc sau
 * nghĩa là đã qua mọi mốc trước. Truyền rỗng để đưa cả chuỗi về chưa đạt.
 */
export async function setReachedProjectTaskMilestone(
  projectId: string,
  taskId: string,
  reachedMilestoneId: string | null,
): Promise<ProjectTaskMilestoneSet> {
  return await request<ProjectTaskMilestoneSet>(
    `${taskRoute(projectId)}/${taskId}/reached-milestone`,
    { method: 'PUT', body: { reachedMilestoneId } },
  );
}

/** Lấy toàn bộ quan hệ phụ thuộc của một dự án. */
export async function listProjectTaskDependencies(
  projectId: string,
): Promise<readonly ProjectTaskDependencyItem[]> {
  return await request<readonly ProjectTaskDependencyItem[]>(dependencyRoute(projectId));
}

/** Khai một quan hệ phụ thuộc. */
export async function addProjectTaskDependency(
  projectId: string,
  predecessorTaskId: string,
  successorTaskId: string,
  dependencyType: ProjectTaskDependencyTypeValue,
  lagDays: number,
  isHardConstraint: boolean,
  note: string | null,
): Promise<ProjectTaskDependencyItem> {
  return await request<ProjectTaskDependencyItem>(dependencyRoute(projectId), {
    method: 'POST',
    body: {
      predecessorTaskId,
      successorTaskId,
      dependencyType,
      lagDays,
      isHardConstraint,
      note,
    },
  });
}

/** Xóa một quan hệ phụ thuộc. */
export async function removeProjectTaskDependency(
  projectId: string,
  dependencyId: string,
): Promise<void> {
  await requestNoContent(`${dependencyRoute(projectId)}/${dependencyId}`, {
    method: 'DELETE',
  });
}

/**
 * Vai trò của một người trong một công việc cụ thể.
 *
 * Khác vai trò trong dự án: vai trò dự án nói người đó làm nghề gì, còn vai trò ở đây nói họ đứng ở
 * đâu trong một việc cụ thể.
 */
export const ProjectTaskAssignmentRole = {
  /** Người chịu trách nhiệm chính. Mỗi việc có đúng một người. */
  Owner: 1,

  /** Người tham gia làm cùng. */
  Contributor: 2,

  /** Người kiểm lại kết quả trước khi coi là xong. */
  Reviewer: 3,

  /** Người chỉ cần được thông báo, không làm gì. */
  Watcher: 4,
} as const;

/** Giá trị của vai trò trong công việc. */
export type ProjectTaskAssignmentRoleValue =
  (typeof ProjectTaskAssignmentRole)[keyof typeof ProjectTaskAssignmentRole];

/** Tên tiếng Việt của từng vai trò trong công việc. */
export const projectTaskAssignmentRoleNames: Readonly<
  Record<ProjectTaskAssignmentRoleValue, string>
> = {
  [ProjectTaskAssignmentRole.Owner]: 'Chịu trách nhiệm chính',
  [ProjectTaskAssignmentRole.Contributor]: 'Tham gia',
  [ProjectTaskAssignmentRole.Reviewer]: 'Kiểm lại',
  [ProjectTaskAssignmentRole.Watcher]: 'Theo dõi',
};

/** Một bản phân công công việc. */
export interface ProjectTaskAssignmentItem {
  readonly id: string;
  readonly projectTaskId: string;
  readonly userAccountId: string;
  readonly role: ProjectTaskAssignmentRoleValue;
  readonly allocationPercent: number | null;
  readonly plannedEffortHours: number | null;
  readonly assignedAt: string;
  readonly assignedByUserId: string;
  readonly acceptedAt: string | null;

  /** Người được giao đã xác nhận nhận việc. */
  readonly isAccepted: boolean;
}

/** Một dòng của màn hình việc của tôi. */
export interface MyProjectTaskItem {
  readonly assignmentId: string;
  readonly taskId: string;
  readonly projectId: string;
  readonly projectCode: string;
  readonly projectName: string;
  readonly wbsCode: string;
  readonly taskName: string;
  readonly role: ProjectTaskAssignmentRoleValue;
  readonly status: ProjectTaskStatusValue;
  readonly blockedReason: string | null;
  readonly priority: ProjectPriorityValue;
  readonly progressPercent: number;
  readonly progressMethod: ProjectTaskProgressMethodValue;
  readonly isProgressSelfReported: boolean;
  readonly plannedEndDate: string | null;
  readonly remainingEffortHours: number | null;
  readonly isOverdue: boolean;
  readonly isAccepted: boolean;
}

/** Lấy danh sách người được giao một công việc. */
export async function listProjectTaskAssignments(
  projectId: string,
  taskId: string,
): Promise<readonly ProjectTaskAssignmentItem[]> {
  return await request<readonly ProjectTaskAssignmentItem[]>(
    `${taskRoute(projectId)}/${taskId}/assignments`,
  );
}

/** Giao một người vào một công việc. */
export async function assignProjectTask(
  projectId: string,
  taskId: string,
  userAccountId: string,
  role: ProjectTaskAssignmentRoleValue,
  allocationPercent: number | null,
  plannedEffortHours: number | null,
): Promise<ProjectTaskAssignmentItem> {
  return await request<ProjectTaskAssignmentItem>(
    `${taskRoute(projectId)}/${taskId}/assignments`,
    {
      method: 'POST',
      body: { userAccountId, role, allocationPercent, plannedEffortHours },
    },
  );
}

/** Sửa vai trò và giờ công dự kiến của một người trong công việc. */
export async function updateProjectTaskAssignment(
  projectId: string,
  taskId: string,
  assignmentId: string,
  role: ProjectTaskAssignmentRoleValue,
  allocationPercent: number | null,
  plannedEffortHours: number | null,
): Promise<ProjectTaskAssignmentItem> {
  return await request<ProjectTaskAssignmentItem>(
    `${taskRoute(projectId)}/${taskId}/assignments/${assignmentId}`,
    {
      method: 'PUT',
      body: { role, allocationPercent, plannedEffortHours },
    },
  );
}

/** Gỡ một người khỏi công việc. */
export async function unassignProjectTask(
  projectId: string,
  taskId: string,
  assignmentId: string,
): Promise<void> {
  await requestNoContent(
    `${taskRoute(projectId)}/${taskId}/assignments/${assignmentId}`,
    { method: 'DELETE' },
  );
}

/** Xác nhận nhận việc. Chỉ chính người được giao mới gọi được. */
export async function acceptProjectTaskAssignment(
  projectId: string,
  taskId: string,
  assignmentId: string,
): Promise<ProjectTaskAssignmentItem> {
  return await request<ProjectTaskAssignmentItem>(
    `${taskRoute(projectId)}/${taskId}/assignments/${assignmentId}/accept`,
    { method: 'POST' },
  );
}

/** Lấy công việc được giao cho chính người đang đăng nhập. */
export async function listMyProjectTasks(
  includeFinished: boolean,
): Promise<readonly MyProjectTaskItem[]> {
  const query = includeFinished ? '?includeFinished=true' : '';

  return await request<readonly MyProjectTaskItem[]>(
    `/api/v1/projects/my-tasks${query}`,
  );
}

/**
 * Loại cảnh báo. Giá trị số phải khớp với máy chủ.
 *
 * Không dùng để hiển thị — câu chữ hiển thị do máy chủ dựng, vì chính máy chủ mới biết con số. Ở
 * đây chỉ dùng làm khóa của dòng và để chọn nơi bấm vào sẽ đi tới.
 */
export const ProjectAlertKind = {
  MyTaskOverdue: 1,
  MyTaskNotAccepted: 2,
  MyTaskBlocked: 3,
  ProjectOverdue: 4,
  ProjectUnhealthy: 5,
  WarrantyExpiringSoon: 6,
  TeamTaskBlocked: 7,
  SuspiciousProgress: 8,
  MyTaskHasNewComment: 9,
  MentionedInComment: 10,
} as const;

/** Giá trị hợp lệ của loại cảnh báo. */
export type ProjectAlertKindValue =
  (typeof ProjectAlertKind)[keyof typeof ProjectAlertKind];

/** Mức độ nghiêm trọng của một cảnh báo. Giá trị số phải khớp với máy chủ. */
export const ProjectAlertSeverity = {
  Information: 1,
  Warning: 2,
  Critical: 3,
} as const;

/** Giá trị hợp lệ của mức độ nghiêm trọng. */
export type ProjectAlertSeverityValue =
  (typeof ProjectAlertSeverity)[keyof typeof ProjectAlertSeverity];

/** Một dòng cảnh báo. */
export interface ProjectAlert {
  readonly kind: ProjectAlertKindValue;
  readonly severity: ProjectAlertSeverityValue;
  readonly count: number;
  readonly message: string;
  readonly detail: string;
  readonly projectId: string | null;
}

/** Toàn bộ cảnh báo của người đang đăng nhập. */
export interface ProjectAlertSummary {
  readonly alerts: readonly ProjectAlert[];
  readonly criticalCount: number;
  readonly totalCount: number;
}

/**
 * Lấy những việc cần chú ý của người đang đăng nhập.
 *
 * Máy chủ tính lại từ dữ liệu thật ở mỗi lần gọi nên không có cờ đã đọc: cảnh báo tự biến mất khi
 * vấn đề được xử lý. Vì vậy phía giao diện cũng không lưu gì lại.
 */
export async function getProjectAlerts(): Promise<ProjectAlertSummary> {
  return await request<ProjectAlertSummary>('/api/v1/projects/alerts');
}

/**
 * Các dấu hiệu cho thấy một lần báo tiến độ không đáng tin.
 *
 * Là <b>tập cờ</b>, không phải giá trị rời rạc: một lần báo có thể mang nhiều dấu hiệu cùng lúc, và
 * mỗi dấu hiệu dẫn tới một câu hỏi khác nhau mà người quản lý cần đặt ra.
 */
export const ProjectTaskProgressSuspicion = {
  None: 0,
  Stalled: 1,
  ContradictsRemainingEffort: 2,
  SuddenJump: 4,
} as const;

/** Câu hỏi tương ứng với từng dấu hiệu, hiển thị cho người đọc. */
export const projectTaskProgressSuspicionLabels: Readonly<Record<number, string>> = {
  [ProjectTaskProgressSuspicion.Stalled]: 'Đứng yên qua ba lần báo liên tiếp',
  [ProjectTaskProgressSuspicion.ContradictsRemainingEffort]:
    'Báo gần xong nhưng giờ công còn lại vẫn nhiều',
  [ProjectTaskProgressSuspicion.SuddenJump]: 'Con số nhảy vọt trong một lần báo',
};

/** Một dòng trong nhật ký báo tiến độ. */
export interface ProjectTaskProgressLogItem {
  readonly id: string;
  readonly projectTaskId: string;
  readonly taskWbsCode: string;
  readonly taskName: string;
  readonly reportedAt: string;
  readonly reportedByUserId: string;
  readonly previousProgressPercent: number | null;
  readonly newProgressPercent: number;
  readonly newRemainingEffortHours: number | null;
  readonly progressMethodUsed: ProjectTaskProgressMethodValue;
  readonly statusAtReport: ProjectTaskStatusValue;
  readonly suspicionSignals: number;
  readonly isSuspicious: boolean;
  readonly note: string | null;
}

/** Lấy lịch sử báo tiến độ của một công việc, mới nhất trước. */
export async function listProjectTaskProgressLog(
  projectId: string,
  taskId: string,
): Promise<readonly ProjectTaskProgressLogItem[]> {
  return await request<readonly ProjectTaskProgressLogItem[]>(
    `${taskRoute(projectId)}/${taskId}/progress-log`,
  );
}

/** Lấy những lần báo tiến độ có dấu hiệu không đáng tin trong cả dự án. */
export async function listSuspiciousProjectProgress(
  projectId: string,
): Promise<readonly ProjectTaskProgressLogItem[]> {
  return await request<readonly ProjectTaskProgressLogItem[]>(
    `${taskRoute(projectId)}/suspicious-progress`,
  );
}

/** Một bình luận trong công việc. */
export interface ProjectTaskComment {
  readonly id: string;
  readonly projectTaskId: string;
  readonly authorUserId: string;
  readonly body: string;
  readonly postedAt: string;
  readonly editedAt: string | null;
  readonly isEdited: boolean;

  /** Người đang xem có phải người viết không. */
  readonly isMine: boolean;

  /** Những người được nhắc tên trong bình luận này. */
  readonly mentionedUserIds: readonly string[];

  /** Người đang xem có xóa được bình luận này không. Do máy chủ tính, giao diện không tự suy. */
  readonly canDelete: boolean;
}

/** Lấy dòng trao đổi của một công việc, cũ nhất trước. */
export async function listProjectTaskComments(
  projectId: string,
  taskId: string,
): Promise<readonly ProjectTaskComment[]> {
  return await request<readonly ProjectTaskComment[]>(
    `${taskRoute(projectId)}/${taskId}/comments`,
  );
}

/** Viết một bình luận. */
export async function postProjectTaskComment(
  projectId: string,
  taskId: string,
  body: string,
  mentionedUserIds: readonly string[] = [],
): Promise<ProjectTaskComment> {
  return await request<ProjectTaskComment>(`${taskRoute(projectId)}/${taskId}/comments`, {
    method: 'POST',
    body: { body, mentionedUserIds },
  });
}

/** Sửa một bình luận. Chỉ người viết mới gọi được. */
export async function editProjectTaskComment(
  projectId: string,
  taskId: string,
  commentId: string,
  body: string,
): Promise<ProjectTaskComment> {
  return await request<ProjectTaskComment>(
    `${taskRoute(projectId)}/${taskId}/comments/${commentId}`,
    { method: 'PUT', body: { body } },
  );
}

/** Xóa một bình luận. */
export async function deleteProjectTaskComment(
  projectId: string,
  taskId: string,
  commentId: string,
): Promise<void> {
  await requestNoContent(`${taskRoute(projectId)}/${taskId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

/** Giới hạn dung lượng một tệp đính kèm, tính bằng megabyte. Phải khớp với máy chủ. */
export const attachmentMaximumFileSizeMegabytes = 10;

/** Giới hạn dung lượng một tệp đính kèm, tính bằng byte. */
export const attachmentMaximumFileSizeBytes = attachmentMaximumFileSizeMegabytes * 1024 * 1024;

/**
 * Các phần mở rộng được phép tải lên. Phải khớp với danh sách của máy chủ.
 *
 * <b>Đây KHÔNG phải một lớp bảo vệ</b> — máy chủ kiểm lại toàn bộ. Danh sách ở đây chỉ để trình
 * duyệt lọc sẵn trong hộp thoại chọn tệp, và để người dùng khỏi chờ tải lên rồi mới bị từ chối.
 */
export const attachmentAllowedExtensions = [
  '.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.tif', '.tiff',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt', '.csv', '.md',
  '.dwg', '.dxf', '.step', '.stp', '.iges', '.igs',
  '.zip', '.rar', '.7z',
  '.mp4',
] as const;

/** Một tệp đính kèm trong công việc. */
export interface ProjectTaskAttachment {
  readonly id: string;
  readonly projectTaskId: string;
  readonly fileName: string;
  readonly contentType: string;
  readonly sizeBytes: number;
  readonly uploadedByUserId: string;
  readonly uploadedAt: string;
  readonly description: string | null;

  /** Người đang xem có xóa được tệp này không. Do máy chủ tính. */
  readonly canDelete: boolean;
}

/** Lấy danh sách tệp đính kèm của một công việc, mới nhất trước. */
export async function listProjectTaskAttachments(
  projectId: string,
  taskId: string,
): Promise<readonly ProjectTaskAttachment[]> {
  return await request<readonly ProjectTaskAttachment[]>(
    `${taskRoute(projectId)}/${taskId}/attachments`,
  );
}

/** Tải một tệp lên công việc. */
export async function uploadProjectTaskAttachment(
  projectId: string,
  taskId: string,
  file: File,
  description: string | null,
): Promise<ProjectTaskAttachment> {
  const form = new FormData();

  form.append('file', file);

  if (description !== null && description.trim().length > 0) {
    form.append('description', description);
  }

  return await requestForm<ProjectTaskAttachment>(
    `${taskRoute(projectId)}/${taskId}/attachments`,
    form,
  );
}

/** Tải một tệp đính kèm về dưới dạng khối nhị phân. */
export async function downloadProjectTaskAttachment(
  projectId: string,
  taskId: string,
  attachmentId: string,
): Promise<Blob> {
  return await requestBlob(`${taskRoute(projectId)}/${taskId}/attachments/${attachmentId}`);
}

/** Xóa một tệp đính kèm. */
export async function deleteProjectTaskAttachment(
  projectId: string,
  taskId: string,
  attachmentId: string,
): Promise<void> {
  await requestNoContent(`${taskRoute(projectId)}/${taskId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}
